import React, { useState, useEffect } from 'react';
import { getAuth } from '../Config/matuAuth';
import { getDatabase, ref, get } from '../Config/matuRtdb';
import { 
  Users, 
  Target, 
  BarChart3, 
  Settings, 
  Upload, 
  Crown, 
  Shield, 
  Activity,
  TrendingUp,
  Calendar,
  DollarSign,
  MessageSquare,
  LogOut,
  Menu,
  X,
  Home,
  PieChart,
  Zap,
  Bell
} from 'lucide-react';

// Componentes del dashboard
import PronosticosManager from './components/PronosticosManager';
import BulkPronosticsUpload from './components/BulkPronosticsUpload';
import UserManager from './components/UserManager';
import Analytics from './components/Analytics';
import PredictionIntelligence from './components/PredictionIntelligence';
import DashboardOverview from './components/DashboardOverview';
import AdvancedMetrics from './components/AdvancedMetrics';
import ChatManager from './components/ChatManager';
import SupportMetrics from './components/SupportMetrics';
import NotificationManager from './components/NotificationManager';
import FinancialReports from './components/FinancialReports';
import AIPredictions from './components/AIPredictions';

const SuperAdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [userData, setUserData] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const db = getDatabase();
    const user = auth.currentUser;

    if (user) {
      // Obtener datos del usuario actual
      const userRef = ref(db, `usuarios/${user.uid}`);
      get(userRef).then((snapshot) => {
        if (snapshot.exists()) {
          setUserData(snapshot.val());
        }
      });
    }
  }, []);

  const handleLogout = () => {
    const auth = getAuth();
    auth.signOut();
    window.location.href = '/prediccion-zone/start';
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'pronosticos', label: 'Pronósticos', icon: Target },
    { id: 'carga-masiva', label: 'Carga Masiva', icon: Upload },
    { id: 'ai-predictions', label: 'Pronósticos IA', icon: Zap },
    { id: 'usuarios', label: 'Usuarios', icon: Users },
    { id: 'chats', label: 'Chats de Soporte', icon: MessageSquare },
    { id: 'notificaciones', label: 'Notificaciones', icon: Bell },
    { id: 'financiero', label: 'Reportes Financieros', icon: DollarSign },
    { id: 'inteligencia', label: 'Inteligencia', icon: TrendingUp },
    { id: 'analytics', label: 'Analytics', icon: Activity },
    { id: 'metricas', label: 'Métricas Avanzadas', icon: PieChart },
    { id: 'soporte', label: 'Métricas Soporte', icon: Shield }
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <DashboardOverview />;
      case 'pronosticos':
        return <PronosticosManager />;
      case 'carga-masiva':
        return <BulkPronosticsUpload />;
      case 'ai-predictions':
        return <AIPredictions />;
      case 'usuarios':
        return <UserManager />;
      case 'chats':
        return <ChatManager />;
      case 'notificaciones':
        return <NotificationManager />;
      case 'financiero':
        return <FinancialReports />;
      case 'inteligencia':
        return <PredictionIntelligence />;
      case 'analytics':
        return <Analytics />;
      case 'metricas':
        return <AdvancedMetrics />;
      case 'soporte':
        return <SupportMetrics />;
      default:
        return <DashboardOverview />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-lg border-b border-white/20 sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-white lg:hidden p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            <div className="flex items-center space-x-2">
              <div className="bg-gradient-to-r from-yellow-400 to-orange-500 p-2 rounded-lg">
                <Crown className="text-white" size={20} />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-lg sm:text-xl font-bold text-white">Super Admin</h1>
                <p className="text-white/60 text-xs">Panel de Control</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-2 sm:space-x-4">
            <div className="hidden md:flex items-center space-x-2 text-white bg-white/10 px-3 py-1 rounded-lg">
              <Shield className="text-green-400" size={16} />
              <span className="text-sm font-medium">Super Admin</span>
            </div>
            {userData && (
              <div className="hidden sm:flex items-center space-x-2 text-white">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                  <span className="text-sm font-bold">
                    {userData.name ? userData.name.charAt(0).toUpperCase() : 'A'}
                  </span>
                </div>
                <span className="text-sm font-medium">{userData.name || 'Admin'}</span>
              </div>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg transition-colors text-sm"
            >
              <LogOut size={16} />
              <span className="hidden sm:inline">Salir</span>
            </button>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <div className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white/10 backdrop-blur-lg border-r border-white/20 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}>
          <div className="p-4">
            <div className="mb-6">
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-4 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="bg-white/20 p-2 rounded-lg">
                    <Crown className="text-white" size={20} />
                  </div>
                  <div>
                    <h3 className="text-white font-bold">Super Admin</h3>
                    <p className="text-white/80 text-sm">Panel de Control</p>
                  </div>
                </div>
              </div>
            </div>

            <nav className="space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id);
                      setSidebarOpen(false);
                    }}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                      activeTab === tab.id
                        ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg'
                        : 'text-white hover:bg-white/10 hover:scale-105'
                    }`}
                  >
                    <Icon size={20} />
                    <span className="font-medium">{tab.label}</span>
                  </button>
                );
              })}
            </nav>

            {/* Información adicional */}
            <div className="mt-8 p-4 bg-white/5 rounded-lg border border-white/10">
              <h4 className="text-white font-semibold mb-2 text-sm">Acciones Rápidas</h4>
              <div className="space-y-2">
                <button 
                  onClick={() => {
                    setActiveTab('pronosticos');
                    setSidebarOpen(false);
                  }}
                  className="w-full flex items-center space-x-2 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm"
                >
                  <Target size={16} />
                  <span>Nuevo Pronóstico</span>
                </button>
                <button 
                  onClick={() => {
                    setActiveTab('carga-masiva');
                    setSidebarOpen(false);
                  }}
                  className="w-full flex items-center space-x-2 px-3 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors text-sm"
                >
                  <Upload size={16} />
                  <span>Carga Masiva Excel</span>
                </button>
                <button 
                  onClick={() => {
                    setActiveTab('usuarios');
                    setSidebarOpen(false);
                  }}
                  className="w-full flex items-center space-x-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
                >
                  <Users size={16} />
                  <span>Gestionar Usuarios</span>
                </button>
                <button 
                  onClick={() => {
                    setActiveTab('metricas');
                    setSidebarOpen(false);
                  }}
                  className="w-full flex items-center space-x-2 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm"
                >
                  <Zap size={16} />
                  <span>Ver Métricas</span>
                </button>
              </div>
            </div>

            {/* Estadísticas rápidas */}
            <div className="mt-6 p-4 bg-white/5 rounded-lg border border-white/10">
              <h4 className="text-white font-semibold mb-3 text-sm">Estadísticas Rápidas</h4>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-white/60">Usuarios VIP</span>
                  <span className="text-yellow-400 font-semibold">--</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Pronósticos Hoy</span>
                  <span className="text-green-400 font-semibold">--</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Tasa de Éxito</span>
                  <span className="text-blue-400 font-semibold">--%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          <div className="p-4 lg:p-6">
            {/* Breadcrumb */}
            <div className="mb-6">
              <nav className="flex items-center space-x-2 text-white/60 text-sm">
                <span>Dashboard</span>
                <span>/</span>
                <span className="text-white font-medium">
                  {tabs.find(tab => tab.id === activeTab)?.label || 'Dashboard'}
                </span>
              </nav>
            </div>

            {/* Content */}
            <div className="animate-fadeIn">
              {renderContent()}
            </div>
          </div>
        </div>
      </div>

      {/* Overlay para móvil */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Estilos CSS para animaciones */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default SuperAdminDashboard; 